// =================== 1. COMPOSANT SCANNER À AJOUTER ===================
// À créer dans: components/common/SaleInterfaceScanner.js

import React, { useState, useRef, useEffect } from 'react';
import { InputGroup, Form, Button, Alert, ListGroup } from 'react-bootstrap';
import { Search, Camera, X, CheckCircle, AlertCircle } from 'lucide-react';

const SaleInterfaceScanner = ({ 
  searchTerm, 
  setSearchTerm, 
  searchRef,
  products = [],
  onProductScanned, 
  addToCart, 
  placeholder = "🔍 Rechercher un produit..."
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastScanResult, setLastScanResult] = useState(null);

  // Suggestions en temps réel
  useEffect(() => {
    if (searchTerm.length >= 2 && products.length > 0) {
      const filtered = products.filter(product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.barcode?.includes(searchTerm)
      ).slice(0, 6);

      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, products]);

  // Fonction de scan/recherche
  const handleScanProduct = (searchValue = searchTerm) => {
    if (!searchValue.trim()) return;

    const trimmedValue = searchValue.trim();
    
    // Recherche exacte d'abord (code-barres ou SKU)
    let foundProduct = products.find(p => 
      p.barcode === trimmedValue || 
      p.sku === trimmedValue
    );

    // Si pas trouvé, recherche partielle dans le nom
    if (!foundProduct) {
      foundProduct = products.find(p =>
        p.name?.toLowerCase().includes(trimmedValue.toLowerCase())
      );
    }

    if (foundProduct) {
      
      setLastScanResult({
        type: 'success',
        message: `✅ ${foundProduct.name} ajouté au panier`,
        product: foundProduct
      });
      
      // Utiliser votre fonction addToCart existante
      addToCart(foundProduct);
      onProductScanned?.(foundProduct);
      
      // Nettoyer et refocus
      setSearchTerm('');
      setShowSuggestions(false);
      
      setTimeout(() => {
        if (searchRef.current) {
          searchRef.current.focus();
        }
        setLastScanResult(null);
      }, 2000);
      
    } else {
      setLastScanResult({
        type: 'error',
        message: `❌ Aucun produit trouvé: ${trimmedValue}`,
        product: null
      });
      
      setTimeout(() => {
        setLastScanResult(null);
      }, 3000);
    }
  };

  // Gestion des touches
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0 && showSuggestions) {
        selectSuggestion(suggestions[0]);
      } else {
        handleScanProduct();
      }
    } else if (e.key === 'Escape') {
      setSearchTerm('');
      setShowSuggestions(false);
      setLastScanResult(null);
    }
  };

  // Sélection d'une suggestion
  const selectSuggestion = (product) => {
    
    addToCart(product);
    onProductScanned?.(product);
    
    setLastScanResult({
      type: 'success',
      message: `✅ ${product.name} ajouté au panier`,
      product: product
    });
    
    setSearchTerm('');
    setShowSuggestions(false);
    
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
      setLastScanResult(null);
    }, 2000);
  };

  // Simulation scan caméra
  const simulateCameraScan = () => {
    setIsScanning(true);
    
    setTimeout(() => {
      if (products.length > 0) {
        const productsWithBarcodes = products.filter(p => p.barcode);
        if (productsWithBarcodes.length > 0) {
          const randomProduct = productsWithBarcodes[Math.floor(Math.random() * productsWithBarcodes.length)];
          handleScanProduct(randomProduct.barcode);
        } else {
          setLastScanResult({
            type: 'error',
            message: '❌ Aucun produit avec code-barres disponible',
            product: null
          });
          setTimeout(() => setLastScanResult(null), 3000);
        }
      }
      setIsScanning(false);
    }, 2000);
  };

  // Effacer la recherche
  const clearSearch = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    setLastScanResult(null);
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  return (
    <div className="position-relative">
      {/* INPUT GROUP - Remplace votre InputGroup existant */}
      <InputGroup className="search-input mb-3">
        <InputGroup.Text>
          <Search size={16} />
        </InputGroup.Text>
        <Form.Control
          ref={searchRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
          className="search-field"
          disabled={isScanning}
          style={{ 
            borderRight: suggestions.length > 0 ? 'none' : '',
            backgroundColor: isScanning ? '#f8f9fa' : ''
          }}
        />
        
        {/* Bouton effacer */}
        {searchTerm && !isScanning && (
          <Button 
            variant="outline-secondary"
            onClick={clearSearch}
            title="Effacer"
          >
            <X size={16} />
          </Button>
        )}
        
        {/* Bouton scan direct */}
        <Button 
          variant="primary"
          onClick={() => handleScanProduct()}
          disabled={!searchTerm.trim() || isScanning}
          title="Scanner ce code"
        >
          <Search size={16} />
        </Button>
        
        {/* Bouton caméra */}
        <Button 
          variant="outline-primary"
          onClick={simulateCameraScan}
          disabled={isScanning}
          title="Scanner avec caméra"
        >
          <Camera size={16} />
        </Button>
      </InputGroup>

      {/* Suggestions en temps réel */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{ 
            zIndex: 1050, 
            top: '100%', 
            marginTop: '-1rem',
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          <div className="p-2 bg-light border-bottom">
            <small className="text-muted fw-bold">
              📋 Suggestions ({suggestions.length})
            </small>
          </div>
          <ListGroup variant="flush">
            {suggestions.map((product, index) => (
              <ListGroup.Item
                key={product.id}
                action
                onClick={() => selectSuggestion(product)}
                className="py-2"
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="fw-semibold small">{product.name}</div>
                    <small className="text-muted">
                      {product.sku && `SKU: ${product.sku} • `}
                      {product.barcode && `Code: ${product.barcode} • `}
                      Stock: {product.stock || product.currentStock || 0}
                    </small>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold text-primary small">
                      {product.price ? 
                        new Intl.NumberFormat('fr-FR').format(product.price) + ' F' : 
                        'N/A'
                      }
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
          <div className="p-2 bg-light text-center">
            <small className="text-muted">
              💡 Cliquez pour ajouter au panier • Entrée = Premier résultat
            </small>
          </div>
        </div>
      )}

      {/* Messages de retour */}
      {lastScanResult && (
        <Alert 
          variant={lastScanResult.type === 'success' ? 'success' : 'danger'} 
          className="py-2 mb-3"
          style={{ marginTop: showSuggestions ? '320px' : '0' }}
        >
          <div className="d-flex align-items-center">
            {lastScanResult.type === 'success' ? 
              <CheckCircle size={16} className="me-2" /> : 
              <AlertCircle size={16} className="me-2" />
            }
            <span className="small fw-semibold">{lastScanResult.message}</span>
          </div>
        </Alert>
      )}

      {/* Indicateur de scan caméra */}
      {isScanning && (
        <Alert variant="info" className="py-2 mb-3">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Scanning...</span>
            </div>
            <span className="small">📱 Scan caméra en cours...</span>
          </div>
        </Alert>
      )}

      {/* Conseils d'utilisation */}
      {!lastScanResult && !isScanning && !showSuggestions && searchTerm.length === 0 && (
        <div className="text-muted small mb-2">
          💡 <strong>Scanner rapide:</strong> Saisissez un code-barres/SKU et appuyez sur Entrée
        </div>
      )}
    </div>
  );
};

export default SaleInterfaceScanner;

